// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '@/components/forms';
import { toast } from 'sonner';
import type { UsersApi } from '../../services/users-api';
import { useApplications } from '../../services/templates-api';
import type {
  ConnectorAdminGrantsClient,
  ConnectorProvider,
} from '@/subsystems/vault';
import {
  ConnectorAdminRoleCheckboxes,
  reconcileConnectorAdminGrants,
} from '@/subsystems/vault';
import { RoleCheckboxes } from './RoleCheckboxes';

interface EditOrgRolesDialogProps {
  orgId: string;
  userId: string;
  initialRoles: string[];
  usersApi: UsersApi;
  connectorProviders: readonly ConnectorProvider[];
  initialConnectorProviders: readonly ConnectorProvider[];
  connectorRolesLoading: boolean;
  connectorRolesError: boolean;
  connectorGrantsApi: ConnectorAdminGrantsClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditOrgRolesDialog({
  orgId,
  userId,
  initialRoles,
  usersApi,
  connectorProviders,
  initialConnectorProviders,
  connectorRolesLoading,
  connectorRolesError,
  connectorGrantsApi,
  open,
  onOpenChange,
}: EditOrgRolesDialogProps) {
  const queryClient = useQueryClient();

  const [roles, setRoles] = useState<string[]>(initialRoles);
  const [selectedConnectorProviders, setSelectedConnectorProviders] = useState<ConnectorProvider[]>(
    [...initialConnectorProviders],
  );

  useEffect(() => {
    if (open) {
      setRoles(initialRoles);
      setSelectedConnectorProviders([...initialConnectorProviders]);
    }
  }, [open, initialRoles, initialConnectorProviders]);

  const { data: applications = [] } = useApplications(open);

  const updateRolesMutation = useMutation({
    mutationFn: async () => {
      // cm-coding: This user-centered editor composes organization membership
      // with Vault-owned provider grants. Keep the existing records and checks
      // separate so connector authority retains its audit and restore rules.
      await Promise.all([
        usersApi.updateUserOrgRoles(orgId, userId, roles),
        reconcileConnectorAdminGrants(
          connectorGrantsApi,
          orgId,
          userId,
          initialConnectorProviders,
          selectedConnectorProviders,
        ),
      ]);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users', orgId] }),
        queryClient.invalidateQueries({ queryKey: ['org-members', orgId] }),
        queryClient.invalidateQueries({ queryKey: ['connector-admin-grants', orgId] }),
      ]);
      toast.success('Member roles updated');
      onOpenChange(false);
    },
    onError: async (error) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users', orgId] }),
        queryClient.invalidateQueries({ queryKey: ['connector-admin-grants', orgId] }),
      ]);
      toast.error('Failed to update roles', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Organization Roles"
      description={userId}
      onSubmit={() => updateRolesMutation.mutate()}
      submitting={updateRolesMutation.isPending}
      canSubmit={!connectorRolesLoading && !connectorRolesError}
      submitTestId="save-org-roles"
      size="md"
    >
      <RoleCheckboxes
        roles={roles}
        onChange={setRoles}
        applications={applications}
      />
      {connectorRolesError ? (
        <p role="alert" className="text-sm text-destructive">
          Connector roles could not be loaded. Close the dialog and try again.
        </p>
      ) : (
        <ConnectorAdminRoleCheckboxes
          providers={connectorProviders}
          selectedProviders={selectedConnectorProviders}
          onChange={setSelectedConnectorProviders}
          disabled={connectorRolesLoading || updateRolesMutation.isPending}
        />
      )}
    </FormDialog>
  );
}
